import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, ApiError } from "./api/client";
import { Layout } from "./components/Layout";
import { AccessDenied } from "./pages/AccessDenied";
import { Home } from "./pages/Home";
import { Placeholder } from "./pages/Placeholder";
import { MediaGrid } from "./pages/MediaGrid";
import { MediaDetail } from "./pages/MediaDetail";
import { Upload } from "./pages/Upload";
import { Generate } from "./pages/Generate";
import { Favorites } from "./pages/Favorites";
import { Tags } from "./pages/Tags";
import { Admin } from "./pages/Admin";
import { Trash } from "./pages/Trash";
import { Playlists } from "./pages/Playlists";
import { PlaylistDetail } from "./pages/PlaylistDetail";
import { SelectionProvider } from "./components/SelectionContext";
import { LocalLogin } from "./components/LocalLogin";

function usePathname() {
  const [path, setPath] = useState(window.location.pathname);
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  return path;
}

function useSearch(): URLSearchParams {
  const [search, setSearch] = useState(window.location.search);
  useEffect(() => {
    const onPop = () => setSearch(window.location.search);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  return new URLSearchParams(search);
}

export function App() {
  const path = usePathname();
  const search = useSearch();

  const configQuery = useQuery({
    queryKey: ["config"],
    queryFn: api.config,
    staleTime: 60_000,
  });
  const appName = configQuery.data?.appName ?? "Media Viewer";

  // Keep the browser tab title in sync with the configured app name.
  // We also surface the page section as a prefix when we have one.
  useEffect(() => {
    const section = sectionFromPath(path);
    document.title = section ? `${section} · ${appName}` : appName;
  }, [appName, path]);

  if (path === "/access-denied") return <AccessDenied />;

  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: api.me,
    retry: false,
  });

  if (meQuery.isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center text-zinc-500">Loading…</div>
    );
  }

  if (meQuery.isError) {
    const err = meQuery.error;
    if (err instanceof ApiError) {
      if (err.status === 401) {
        if (configQuery.data?.authMode === "local") {
          return <LocalLogin />;
        }
        window.location.href = "/api/auth/login";
        return null;
      }
      if (err.status === 403) return <AccessDenied />;
    }
    return (
      <div className="flex min-h-screen items-center justify-center p-8 text-red-300">
        Error loading user: {String(err)}
      </div>
    );
  }

  const me = meQuery.data;
  const homeRefreshSeconds = configQuery.data?.homeRefreshSeconds ?? 600;

  let body: JSX.Element;
  if (path === "/" || path === "") {
    body = <Home isAdmin={me.isAdmin} homeRefreshSeconds={homeRefreshSeconds} />;
  } else if (path === "/photos") {
    body = <MediaGrid kind="photo" set="photos" isAdmin={me.isAdmin} />;
  } else if (path === "/videos") {
    body = <MediaGrid kind="video" set="videos" isAdmin={me.isAdmin} />;
  } else if (path.startsWith("/media/")) {
    const id = Number(path.slice("/media/".length));
    if (Number.isFinite(id)) {
      const set = search.get("set") ?? "all";
      const auto = search.get("auto") ?? null;
      const lightbox = search.get("lightbox") === "1";
      const minSecondsRaw = search.get("minSeconds");
      const maxSecondsRaw = search.get("maxSeconds");
      const minSeconds = minSecondsRaw != null && Number.isFinite(Number(minSecondsRaw))
        ? Number(minSecondsRaw)
        : null;
      const maxSeconds = maxSecondsRaw != null && Number.isFinite(Number(maxSecondsRaw))
        ? Number(maxSecondsRaw)
        : null;
      const q = search.get("q");
      const order = search.get("order");
      const tagsRaw = search.get("tags");
      const excludeTagsRaw = search.get("excludeTags");
      const includeTagSlugs = tagsRaw
        ? tagsRaw.split(",").map((s) => s.trim()).filter(Boolean)
        : [];
      const excludeTagSlugs = excludeTagsRaw
        ? excludeTagsRaw.split(",").map((s) => s.trim()).filter(Boolean)
        : [];
      const filter = search.get("filter");
      const seed = search.get("seed");
      const shuffle = search.get("shuffle") === "1";
      const nextParam = search.get("next");
      const nextMode = nextParam === "folder" || nextParam === "display" ? nextParam : null;
      body = (
        <MediaDetail
          id={id}
          set={set}
          auto={auto}
          lightbox={lightbox}
          minSeconds={minSeconds}
          maxSeconds={maxSeconds}
          q={q}
          order={order}
          includeTagSlugs={includeTagSlugs}
          excludeTagSlugs={excludeTagSlugs}
          filter={filter}
          seed={seed}
          nextMode={nextMode}
          shuffle={shuffle}
          isAdmin={me.isAdmin}
        />
      );
    } else {
      body = <Placeholder title="Not found" hint={`Bad media id in ${path}.`} />;
    }
  } else if (path === "/favorites") {
    body = <Favorites isAdmin={me.isAdmin} />;
  } else if (path === "/tags") {
    body = <Tags isAdmin={me.isAdmin} />;
  } else if (path.startsWith("/tags/")) {
    const slug = decodeURIComponent(path.slice("/tags/".length));
    body = (
      <MediaGrid
        tag={slug}
        set={`tag:${slug}`}
        isAdmin={me.isAdmin}
        emptyHint={`No media with tag "${slug}".`}
      />
    );
  } else if (path === "/playlists") {
    body = <Playlists />;
  } else if (path.startsWith("/playlists/")) {
    const id = Number(path.slice("/playlists/".length));
    if (Number.isFinite(id)) body = <PlaylistDetail id={id} isAdmin={me.isAdmin} />;
    else body = <Placeholder title="Not found" hint={`Bad playlist id in ${path}.`} />;
  } else if (path.startsWith("/upload")) {
    body = <Upload />;
  } else if (path === "/generate" && configQuery.data?.aiGenerationEnabled) {
    body = <Generate />;
  } else if (path === "/admin/trash") {
    if (!me.isAdmin) {
      body = <Placeholder title="Forbidden" hint="Admin access required." />;
    } else {
      body = <Trash />;
    }
  } else if (path.startsWith("/admin")) {
    if (!me.isAdmin) {
      body = <Placeholder title="Forbidden" hint="Admin access required." />;
    } else {
      body = (
        <Admin
          appNameLocked={configQuery.data?.appNameLocked ?? false}
          currentAppName={appName}
          homeRefreshSeconds={homeRefreshSeconds}
          homeRefreshLocked={configQuery.data?.homeRefreshLocked ?? false}
          authMode={configQuery.data?.authMode ?? "dev"}
          currentUserId={me.id}
        />
      );
    }
  } else {
    body = <Placeholder title="Not found" hint={`No route matches ${path}.`} />;
  }

  return (
    <SelectionProvider>
      <Layout
        me={me}
        current={path}
        appName={appName}
        accountUrl={configQuery.data?.accountUrl ?? null}
        authMode={configQuery.data?.authMode ?? "dev"}
        aiEnabled={configQuery.data?.aiGenerationEnabled ?? false}
      >
        {body}
      </Layout>
    </SelectionProvider>
  );
}

function sectionFromPath(path: string): string | null {
  if (path === "/" || path === "") return null;
  if (path.startsWith("/photos")) return "Photos";
  if (path.startsWith("/videos")) return "Videos";
  if (path.startsWith("/favorites")) return "Favorites";
  if (path.startsWith("/playlists")) return "Playlists";
  if (path.startsWith("/tags")) return "Tags";
  if (path.startsWith("/upload")) return "Upload";
  if (path === "/generate") return "Generate";
  if (path === "/admin/trash") return "Trash";
  if (path.startsWith("/admin")) return "Admin";
  if (path.startsWith("/media/")) return "Viewer";
  return null;
}
